<template>
  <div>
    <StackedHorizontalBarChart
      :title="chartTitle"
      :filterContext="filterContext"
      :dataProviders="dataProviders"
      :contextUpdaters="contextUpdaters"
      :navigationConfig="navigationConfig"
      :chartFontColor="chartFontColor"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';
import { executeAndProcessQuery } from '@/utils/stackedBarData';
import { sqlLiteral } from '@/utils/assetFields';

const store = useStore();
const { t } = useI18n();
const filterContext = computed(() => store.state.filterContext);
const chartFontColor = computed(() => store.getters.chartFontColor);
const conn = store.getters.getDBConnection;

const chartName = 'assetTypeStackedBarChart';
const chartTitle = t('assetsByType');

// Unique assets by type, stacked by current status.
const getAssetTypeData = (filtered = true) => executeAndProcessQuery(conn, `
  SELECT
    a.assetType AS id,
    a.assetType AS title,
    s.Title AS status_title,
    s.Color AS color,
    COUNT(*) AS value,
    MIN(s.Sort) AS sort
  FROM ${filtered ? 'filteredAssets' : 'unique_assets'} a
  JOIN status_templates s ON a.assetStatusId = s.Id
  WHERE a.assetType IS NOT NULL
  GROUP BY a.assetType, s.Title, s.Color
  ORDER BY a.assetType, sort, status_title
`);

const getStatusByAssetTypeData = (filtered = true) => executeAndProcessQuery(conn, `
  SELECT
    s.Title AS id,
    a.assetType AS parentId,
    s.Title AS title,
    s.Title AS status_title,
    s.Color AS color,
    COUNT(*) AS value,
    MIN(s.Sort) AS sort
  FROM ${filtered ? 'filteredAssets' : 'unique_assets'} a
  JOIN status_templates s ON a.assetStatusId = s.Id
  WHERE a.assetType IS NOT NULL
  GROUP BY s.Title, a.assetType, s.Color
  ORDER BY sort, s.Title
`);

const getAssetTypeFilterContext = async (assetType) => {
  await filterContext.value.updateFilterWithCondition(
    chartName,
    `assetType = ${sqlLiteral(assetType)}`,
    `${t('assetType')}: ${assetType}`
  );
};

const getStatusFilterContext = async (statusTitle, assetType) => {
  const clauses = [`assetStatusId IN (SELECT Id FROM status_templates WHERE Title = ${sqlLiteral(statusTitle)})`];
  if (assetType) clauses.push(`assetType = ${sqlLiteral(assetType)}`);
  await filterContext.value.updateFilterWithCondition(
    chartName,
    clauses.join(' AND '),
    `${t('status')}: ${statusTitle}`
  );
};

const resetFilterContext = async () => {
  await filterContext.value.removeFilter(chartName);
};

const dataProviders = {
  type: getAssetTypeData,
  status: getStatusByAssetTypeData
};

const contextUpdaters = {
  type: getAssetTypeFilterContext,
  status: getStatusFilterContext,
  reset: resetFilterContext /*required*/
};

const navigationConfig = [
  { level: 'type', titleKey: 'assetType' },
  { level: 'status', titleKey: 'status' }
];
</script>
